<script setup lang="ts">
/**
 * Note: twelve of these names have no branch above and render nothing —
 * `adjust`, `chat with ai`, `dash`, `edit document`, `hidden`, `next`,
 * `notification`, `previous`, `sort ascending`, `sort descending`, `visible`,
 * `warning`. None is used anywhere today, so nothing is broken, but the type
 * promises glyphs that do not exist. Draw one before reaching for it.
 */
export type IconName =
  | "list"
  | "tree"
  | "radio checked"
  | "letter resize"
  | "rotate"
  | "color palette"
  | "plus"
  | "search grid"
  | "edit document"
  | "warning"
  | "hidden"
  | "visible"
  | "check"
  | "left"
  | "right"
  | "chevrons left"
  | "chevrons right"
  | "up"
  | "down"
  | "dash"
  | "replay"
  | "next"
  | "previous"
  | "description"
  | "search"
  | "profile"
  | "settings"
  | "notification"
  | "play dashed"
  | "adjust"
  | "sort"
  | "sort descending"
  | "sort ascending"
  | "tv"
  | "web"
  | "ios"
  | "android"
  | "up right"
  | "down left"
  | "edit"
  | "log out"
  | "chat with ai"
  | "cross"
  | "calendar"
  | "phone"
  | "bug"
  | "document"
  | "folder"
  | "clock"
  | "refresh"
  | "play"
  | "play filled"
  | "pause"
  | "trash"
  | "upload"
  | "upload dashed"
  | "grid"
  | "bar chart"
  | "list lines"
  | "grip"
  | "flask"
  | "calendar clock"
  | "bolt"
  | "device"
  | "box"
  | "user"
  | "sign out"
  | "download"
  | "sparkle"
  | "sun"
  | "moon"
  | "reset clear data"
  | "reset restart"
  | "reset keep"
  | "cost"
  | "lock"
  | "menu"
  | "more"
  | "copy";

/** Shared attributes for the stroked redesign glyphs — they only differ by path. */
const strokedIconAttrs = {
  width: 24,
  height: 24,
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor",
  "stroke-width": 1.8,
  "stroke-linecap": "round",
  "stroke-linejoin": "round",
  xmlns: "http://www.w3.org/2000/svg",
} as const;

const { name } = defineProps<{
  name: IconName;
}>();
</script>

<template>
  <!--
  Icons are Figma exports, each wrapping its shape in a `<mask>` whose rect is the
  full 24×24 box. That rect's fill sets the mask's alpha (`mask-type: alpha`), so it
  must be opaque — `white`, never `currentColor`.

  It was `currentColor`, and that made a glyph's visibility depend on a *different*
  element's colour: the exports also carry hard-coded mask ids, so every instance of
  an icon resolves `url(#mask…)` to the first one in the document. Render a `check`
  transparently anywhere on the page and it zeroed the alpha for every other `check`
  — the tick vanished from a checked filter box and left the blue fill behind.

  The visible paths still use `currentColor`; only the mask rects are opaque.
-->
  <svg
    v-if="name === 'list'"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
  >
    <mask
      id="mask0_101_9321"
      style="mask-type: alpha"
      maskUnits="userSpaceOnUse"
      x="0"
      y="0"
      width="24"
      height="24"
    >
      <rect
        y="6.10352e-05"
        width="24"
        height="24"
        fill="white"
      />
    </mask>
    <g mask="url(#mask0_101_9321)">
      <path
        d="M16 21.0001C15.45 21.0001 14.9792 20.8042 14.5875 20.4126C14.1958 20.0209 14 19.5501 14 19.0001V15.0001C14 14.4501 14.1958 13.9792 14.5875 13.5876C14.9792 13.1959 15.45 13.0001 16 13.0001H20C20.55 13.0001 21.0208 13.1959 21.4125 13.5876C21.8042 13.9792 22 14.4501 22 15.0001V19.0001C22 19.5501 21.8042 20.0209 21.4125 20.4126C21.0208 20.8042 20.55 21.0001 20 21.0001H16ZM16 19.0001H20V15.0001H16V19.0001ZM2 18.0001V16.0001H11V18.0001H2ZM16 11.0001C15.45 11.0001 14.9792 10.8042 14.5875 10.4126C14.1958 10.0209 14 9.55006 14 9.00006V5.00006C14 4.45006 14.1958 3.97923 14.5875 3.58756C14.9792 3.19589 15.45 3.00006 16 3.00006H20C20.55 3.00006 21.0208 3.19589 21.4125 3.58756C21.8042 3.97923 22 4.45006 22 5.00006V9.00006C22 9.55006 21.8042 10.0209 21.4125 10.4126C21.0208 10.8042 20.55 11.0001 20 11.0001H16ZM16 9.00006H20V5.00006H16V9.00006ZM2 8.00006V6.00006H11V8.00006H2Z"
        fill="currentColor"
      />
    </g>
  </svg>

  <svg
    v-else-if="name === 'tree'"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
  >
    <mask
      id="mask0_101_9322"
      style="mask-type: alpha"
      maskUnits="userSpaceOnUse"
      x="0"
      y="0"
      width="24"
      height="24"
    >
      <rect
        y="6.10352e-05"
        width="24"
        height="24"
        fill="white"
      />
    </mask>
    <g mask="url(#mask0_101_9322)">
      <path
        d="M15 21.0001V18.0001H11V8.00006H9V11.0001H2V3.00006H9V6.00006H15V3.00006H22V11.0001H15V8.00006H13V16.0001H15V13.0001H22V21.0001H15ZM17 9.00006H20V5.00006H17V9.00006ZM17 19.0001H20V15.0001H17V19.0001ZM4 9.00006H7V5.00006H4V9.00006Z"
        fill="currentColor"
      />
    </g>
  </svg>

  <svg
    v-else-if="name === 'radio checked'"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
  >
    <mask
      id="mask0_88_10645"
      style="mask-type: alpha"
      maskUnits="userSpaceOnUse"
      x="0"
      y="0"
      width="24"
      height="24"
    >
      <rect
        y="6.10352e-05"
        width="24"
        height="24"
        fill="white"
      />
    </mask>
    <g mask="url(#mask0_88_10645)">
      <path
        d="M12 15.0001C12.8333 15.0001 13.5417 14.7084 14.125 14.1251C14.7083 13.5417 15 12.8334 15 12.0001C15 11.1667 14.7083 10.4584 14.125 9.87506C13.5417 9.29173 12.8333 9.00006 12 9.00006C11.1667 9.00006 10.4583 9.29173 9.875 9.87506C9.29167 10.4584 9 11.1667 9 12.0001C9 12.8334 9.29167 13.5417 9.875 14.1251C10.4583 14.7084 11.1667 15.0001 12 15.0001ZM12 22.0001C10.6167 22.0001 9.31667 21.7376 8.1 21.2126C6.88333 20.6876 5.825 19.9751 4.925 19.0751C4.025 18.1751 3.3125 17.1167 2.7875 15.9001C2.2625 14.6834 2 13.3834 2 12.0001C2 10.6167 2.2625 9.31673 2.7875 8.10006C3.3125 6.88339 4.025 5.82506 4.925 4.92506C5.825 4.02506 6.88333 3.31256 8.1 2.78756C9.31667 2.26256 10.6167 2.00006 12 2.00006C13.3833 2.00006 14.6833 2.26256 15.9 2.78756C17.1167 3.31256 18.175 4.02506 19.075 4.92506C19.975 5.82506 20.6875 6.88339 21.2125 8.10006C21.7375 9.31673 22 10.6167 22 12.0001C22 13.3834 21.7375 14.6834 21.2125 15.9001C20.6875 17.1167 19.975 18.1751 19.075 19.0751C18.175 19.9751 17.1167 20.6876 15.9 21.2126C14.6833 21.7376 13.3833 22.0001 12 22.0001ZM12 20.0001C14.2333 20.0001 16.125 19.2251 17.675 17.6751C19.225 16.1251 20 14.2334 20 12.0001C20 9.76673 19.225 7.87506 17.675 6.32506C16.125 4.77506 14.2333 4.00006 12 4.00006C9.76667 4.00006 7.875 4.77506 6.325 6.32506C4.775 7.87506 4 9.76673 4 12.0001C4 14.2334 4.775 16.1251 6.325 17.6751C7.875 19.2251 9.76667 20.0001 12 20.0001Z"
        fill="currentColor"
      />
    </g>
  </svg>

  <svg
    v-else-if="name === 'letter resize'"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
  >
    <mask
      id="mask0_7_111"
      style="mask-type: alpha"
      maskUnits="userSpaceOnUse"
      x="0"
      y="0"
      width="24"
      height="24"
    >
      <rect
        y="6.10352e-05"
        width="24"
        height="24"
        fill="white"
      />
    </mask>
    <g mask="url(#mask0_7_111)">
      <path
        d="M11 22.0001V16.0001H13V18.0001H21V20.0001H13V22.0001H11ZM3 20.0001V18.0001H9V20.0001H3ZM6.425 14.0001H8.5L9.6 10.9251H14.425L15.5 14.0001H17.575L13.075 2.00006H10.925L6.425 14.0001ZM10.2 9.20006L11.95 4.22506H12.05L13.8 9.20006H10.2Z"
        fill="currentColor"
      />
    </g>
  </svg>

  <svg
    v-else-if="name === 'rotate'"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
  >
    <mask
      id="mask0_7_112"
      style="mask-type: alpha"
      maskUnits="userSpaceOnUse"
      x="0"
      y="0"
      width="24"
      height="24"
    >
      <rect
        y="6.10352e-05"
        width="24"
        height="24"
        fill="white"
      />
    </mask>
    <g mask="url(#mask0_7_112)">
      <path
        d="M2 12C2 10.5334 2.28333 9.17505 2.85 7.92505C3.41667 6.67505 4.19167 5.59172 5.175 4.67505C6.15833 3.75838 7.29167 3.06255 8.575 2.58755C9.85833 2.11255 11.2333 1.92505 12.7 2.02505C12.9833 2.05838 13.2083 2.17922 13.375 2.38755C13.5417 2.59588 13.6 2.84172 13.55 3.12505C13.5 3.40838 13.3625 3.63338 13.1375 3.80005C12.9125 3.96672 12.6583 4.04172 12.375 4.02505C11.225 3.97505 10.1458 4.13755 9.1375 4.51255C8.12917 4.88755 7.24167 5.44172 6.475 6.17505C5.70833 6.90838 5.10417 7.77088 4.6625 8.76255C4.22083 9.75422 4 10.8334 4 12C4 14.2334 4.775 16.125 6.325 17.675C7.875 19.225 9.76667 20 12 20C13.15 20 14.2208 19.7792 15.2125 19.3375C16.2042 18.8959 17.0667 18.2917 17.8 17.525C18.5667 16.725 19.1333 15.8167 19.5 14.8C19.8667 13.7834 20.025 12.725 19.975 11.625C19.9583 11.3417 20.0333 11.0876 20.2 10.8625C20.3667 10.6375 20.5917 10.5 20.875 10.45C21.1583 10.4 21.4042 10.4584 21.6125 10.625C21.8208 10.7917 21.9417 11.0167 21.975 11.3C22.075 12.75 21.8875 14.1167 21.4125 15.4C20.9375 16.6834 20.25 17.8167 19.35 18.8C18.4 19.8334 17.2917 20.625 16.025 21.175C14.7583 21.725 13.4167 22 12 22C10.6167 22 9.31667 21.7375 8.1 21.2125C6.88333 20.6875 5.825 19.975 4.925 19.075C4.025 18.175 3.3125 17.1167 2.7875 15.9C2.2625 14.6834 2 13.3834 2 12ZM18 9.00005C17.1667 9.00005 16.4583 8.70838 15.875 8.12505C15.2917 7.54172 15 6.83338 15 6.00005C15 5.16672 15.2917 4.45838 15.875 3.87505C16.4583 3.29172 17.1667 3.00005 18 3.00005C18.8333 3.00005 19.5417 3.29172 20.125 3.87505C20.7083 4.45838 21 5.16672 21 6.00005C21 6.83338 20.7083 7.54172 20.125 8.12505C19.5417 8.70838 18.8333 9.00005 18 9.00005Z"
        fill="currentColor"
      />
    </g>
  </svg>

  <svg
    v-else-if="name === 'color palette'"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
  >
    <mask
      id="mask0_7_113"
      style="mask-type: alpha"
      maskUnits="userSpaceOnUse"
      x="0"
      y="0"
      width="24"
      height="24"
    >
      <rect
        y="6.10352e-05"
        width="24"
        height="24"
        fill="white"
      />
    </mask>
    <g mask="url(#mask0_7_113)">
      <path
        d="M12 22.0001C10.6333 22.0001 9.34167 21.7376 8.125 21.2126C6.90833 20.6876 5.84583 19.9709 4.9375 19.0626C4.02917 18.1542 3.3125 17.0917 2.7875 15.8751C2.2625 14.6584 2 13.3667 2 12.0001C2 10.6167 2.27083 9.31673 2.8125 8.10006C3.35417 6.88339 4.0875 5.82506 5.0125 4.92506C5.9375 4.02506 7.01667 3.31256 8.25 2.78756C9.48333 2.26256 10.8 2.00006 12.2 2.00006C13.5333 2.00006 14.7917 2.22923 15.975 2.68756C17.1583 3.14589 18.1958 3.77923 19.0875 4.58756C19.9792 5.39589 20.6875 6.35423 21.2125 7.46256C21.7375 8.57089 22 9.76673 22 11.0501C22 12.9667 21.4167 14.4376 20.25 15.4626C19.0833 16.4876 17.6667 17.0001 16 17.0001H14.15C14 17.0001 13.8958 17.0417 13.8375 17.1251C13.7792 17.2084 13.75 17.3001 13.75 17.4001C13.75 17.6001 13.875 17.8876 14.125 18.2626C14.375 18.6376 14.5 19.0667 14.5 19.5501C14.5 20.3834 14.2708 21.0001 13.8125 21.4001C13.3542 21.8001 12.75 22.0001 12 22.0001ZM6.5 13.0001C6.93333 13.0001 7.29167 12.8584 7.575 12.5751C7.85833 12.2917 8 11.9334 8 11.5001C8 11.0667 7.85833 10.7084 7.575 10.4251C7.29167 10.1417 6.93333 10.0001 6.5 10.0001C6.06667 10.0001 5.70833 10.1417 5.425 10.4251C5.14167 10.7084 5 11.0667 5 11.5001C5 11.9334 5.14167 12.2917 5.425 12.5751C5.70833 12.8584 6.06667 13.0001 6.5 13.0001ZM9.5 9.00006C9.93333 9.00006 10.2917 8.85839 10.575 8.57506C10.8583 8.29173 11 7.93339 11 7.50006C11 7.06673 10.8583 6.70839 10.575 6.42506C10.2917 6.14173 9.93333 6.00006 9.5 6.00006C9.06667 6.00006 8.70833 6.14173 8.425 6.42506C8.14167 6.70839 8 7.06673 8 7.50006C8 7.93339 8.14167 8.29173 8.425 8.57506C8.70833 8.85839 9.06667 9.00006 9.5 9.00006ZM14.5 9.00006C14.9333 9.00006 15.2917 8.85839 15.575 8.57506C15.8583 8.29173 16 7.93339 16 7.50006C16 7.06673 15.8583 6.70839 15.575 6.42506C15.2917 6.14173 14.9333 6.00006 14.5 6.00006C14.0667 6.00006 13.7083 6.14173 13.425 6.42506C13.1417 6.70839 13 7.06673 13 7.50006C13 7.93339 13.1417 8.29173 13.425 8.57506C13.7083 8.85839 14.0667 9.00006 14.5 9.00006ZM17.5 13.0001C17.9333 13.0001 18.2917 12.8584 18.575 12.5751C18.8583 12.2917 19 11.9334 19 11.5001C19 11.0667 18.8583 10.7084 18.575 10.4251C18.2917 10.1417 17.9333 10.0001 17.5 10.0001C17.0667 10.0001 16.7083 10.1417 16.425 10.4251C16.1417 10.7084 16 11.0667 16 11.5001C16 11.9334 16.1417 12.2917 16.425 12.5751C16.7083 12.8584 17.0667 13.0001 17.5 13.0001ZM12 20.0001C12.15 20.0001 12.2708 19.9584 12.3625 19.8751C12.4542 19.7917 12.5 19.6834 12.5 19.5501C12.5 19.3167 12.375 19.0417 12.125 18.7251C11.875 18.4084 11.75 17.9334 11.75 17.3001C11.75 16.6001 11.9917 16.0417 12.475 15.6251C12.9583 15.2084 13.55 15.0001 14.25 15.0001H16C17.1 15.0001 18.0417 14.6792 18.825 14.0376C19.6083 13.3959 20 12.4001 20 11.0501C20 9.03339 19.2292 7.35423 17.6875 6.01256C16.1458 4.67089 14.3167 4.00006 12.2 4.00006C9.93333 4.00006 8 4.77506 6.4 6.32506C4.8 7.87506 4 9.76673 4 12.0001C4 14.2167 4.77917 16.1042 6.3375 17.6626C7.89583 19.2209 9.78333 20.0001 12 20.0001Z"
        fill="currentColor"
      />
    </g>
  </svg>

  <svg
    v-else-if="name === 'up right'"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
  >
    <mask
      id="mask0_212_6412"
      style="mask-type: alpha"
      maskUnits="userSpaceOnUse"
      x="0"
      y="0"
      width="24"
      height="24"
    >
      <rect
        y="6.10352e-05"
        width="24"
        height="24"
        fill="white"
      />
    </mask>
    <g mask="url(#mask0_212_6412)">
      <path
        d="M7.4 18.0001L17 8.40006V17.0001H19V5.00006H7V7.00006H15.6L6 16.6001L7.4 18.0001Z"
        fill="currentColor"
      />
    </g>
  </svg>

  <svg
    v-else-if="name === 'down left'"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
  >
    <mask
      id="mask0_215_18368"
      style="mask-type: alpha"
      maskUnits="userSpaceOnUse"
      x="0"
      y="0"
      width="24"
      height="24"
    >
      <rect
        y="6.10352e-05"
        width="24"
        height="24"
        fill="white"
      />
    </mask>
    <g mask="url(#mask0_215_18368)">
      <path
        d="M17.6 5.00006L8 14.6001V6.00006H6V18.0001H18V16.0001H9.4L19 6.40006L17.6 5.00006Z"
        fill="currentColor"
      />
    </g>
  </svg>

  <svg
    v-else-if="name === 'edit'"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="currentColor"
    xmlns="http://www.w3.org/2000/svg"
  >
    <mask
      id="mask0_222_1995"
      style="mask-type: alpha"
      maskUnits="userSpaceOnUse"
      x="0"
      y="0"
      width="24"
      height="24"
    >
      <rect
        y="6.10352e-05"
        width="24"
        height="24"
        fill="white"
      />
    </mask>
    <g mask="url(#mask0_222_1995)">
      <path
        d="M5 19.0001H6.425L16.2 9.22506L14.775 7.80006L5 17.5751V19.0001ZM3 21.0001V16.7501L16.2 3.57506C16.4 3.39173 16.6208 3.25006 16.8625 3.15006C17.1042 3.05006 17.3583 3.00006 17.625 3.00006C17.8917 3.00006 18.15 3.05006 18.4 3.15006C18.65 3.25006 18.8667 3.40006 19.05 3.60006L20.425 5.00006C20.625 5.18339 20.7708 5.40006 20.8625 5.65006C20.9542 5.90006 21 6.15006 21 6.40006C21 6.66673 20.9542 6.92089 20.8625 7.16256C20.7708 7.40423 20.625 7.62506 20.425 7.82506L7.25 21.0001H3ZM15.475 8.52506L14.775 7.80006L16.2 9.22506L15.475 8.52506Z"
        fill="currentColor"
      />
    </g>
  </svg>

  <svg
    v-else-if="name === 'plus'"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="currentColor"
    xmlns="http://www.w3.org/2000/svg"
  >
    <mask
      id="mask0_28_210"
      style="mask-type: alpha"
      maskUnits="userSpaceOnUse"
      x="0"
      y="0"
      width="24"
      height="24"
    >
      <rect
        y="6.10352e-05"
        width="24"
        height="24"
        fill="white"
      />
    </mask>
    <g mask="url(#mask0_28_210)">
      <path
        d="M10.8571 13.1429H4V10.8572H10.8571V4.00006H13.1429V10.8572H20V13.1429H13.1429V20.0001H10.8571V13.1429Z"
        fill="currentColor"
      />
    </g>
  </svg>

  <svg
    v-else-if="name === 'tv'"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="currentColor"
    xmlns="http://www.w3.org/2000/svg"
  >
    <mask
      id="mask0_211_6373"
      style="mask-type: alpha"
      maskUnits="userSpaceOnUse"
      x="0"
      y="0"
      width="24"
      height="24"
    >
      <rect
        y="6.10352e-05"
        width="24"
        height="24"
        fill="white"
      />
    </mask>
    <g mask="url(#mask0_211_6373)">
      <path
        d="M10.325 12.0001C10.6083 12.0001 10.8458 11.9042 11.0375 11.7126C11.2292 11.5209 11.325 11.2834 11.325 11.0001C11.325 10.7167 11.2292 10.4792 11.0375 10.2876C10.8458 10.0959 10.6083 10.0001 10.325 10.0001C10.0417 10.0001 9.80417 10.0959 9.6125 10.2876C9.42083 10.4792 9.325 10.7167 9.325 11.0001C9.325 11.2834 9.42083 11.5209 9.6125 11.7126C9.80417 11.9042 10.0417 12.0001 10.325 12.0001ZM7 12.0001C7.28333 12.0001 7.52083 11.9042 7.7125 11.7126C7.90417 11.5209 8 11.2834 8 11.0001C8 10.7167 7.90417 10.4792 7.7125 10.2876C7.52083 10.0959 7.28333 10.0001 7 10.0001C6.71667 10.0001 6.47917 10.0959 6.2875 10.2876C6.09583 10.4792 6 10.7167 6 11.0001C6 11.2834 6.09583 11.5209 6.2875 11.7126C6.47917 11.9042 6.71667 12.0001 7 12.0001ZM13.675 12.0001C13.9583 12.0001 14.1958 11.9042 14.3875 11.7126C14.5792 11.5209 14.675 11.2834 14.675 11.0001C14.675 10.7167 14.5792 10.4792 14.3875 10.2876C14.1958 10.0959 13.9583 10.0001 13.675 10.0001C13.3917 10.0001 13.1542 10.0959 12.9625 10.2876C12.7708 10.4792 12.675 10.7167 12.675 11.0001C12.675 11.2834 12.7708 11.5209 12.9625 11.7126C13.1542 11.9042 13.3917 12.0001 13.675 12.0001ZM17 12.0001C17.2833 12.0001 17.5208 11.9042 17.7125 11.7126C17.9042 11.5209 18 11.2834 18 11.0001C18 10.7167 17.9042 10.4792 17.7125 10.2876C17.5208 10.0959 17.2833 10.0001 17 10.0001C16.7167 10.0001 16.4792 10.0959 16.2875 10.2876C16.0958 10.4792 16 10.7167 16 11.0001C16 11.2834 16.0958 11.5209 16.2875 11.7126C16.4792 11.9042 16.7167 12.0001 17 12.0001ZM4 16.0001H20V6.00006H4V16.0001ZM12 20.0001C10.35 20.0001 8.9375 19.8876 7.7625 19.6626C6.5875 19.4376 6 19.1667 6 18.8501V18.0001H4C3.45 18.0001 2.97917 17.8042 2.5875 17.4126C2.19583 17.0209 2 16.5501 2 16.0001V6.00006C2 5.45006 2.19583 4.97923 2.5875 4.58756C2.97917 4.19589 3.45 4.00006 4 4.00006H20C20.55 4.00006 21.0208 4.19589 21.4125 4.58756C21.8042 4.97923 22 5.45006 22 6.00006V16.0001C22 16.5501 21.8042 17.0209 21.4125 17.4126C21.0208 17.8042 20.55 18.0001 20 18.0001H18V18.8501C18 19.1667 17.4125 19.4376 16.2375 19.6626C15.0625 19.8876 13.65 20.0001 12 20.0001Z"
        fill="currentColor"
      />
    </g>
  </svg>

  <svg
    v-else-if="name === 'web'"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="currentColor"
    xmlns="http://www.w3.org/2000/svg"
  >
    <mask
      id="mask0_211_6374"
      style="mask-type: alpha"
      maskUnits="userSpaceOnUse"
      x="0"
      y="0"
      width="24"
      height="24"
    >
      <rect
        y="6.10352e-05"
        width="24"
        height="24"
        fill="white"
      />
    </mask>
    <g mask="url(#mask0_211_6374)">
      <path
        d="M12 22.0001C10.6333 22.0001 9.34167 21.7376 8.125 21.2126C6.90833 20.6876 5.84583 19.9709 4.9375 19.0626C4.02917 18.1542 3.3125 17.0917 2.7875 15.8751C2.2625 14.6584 2 13.3667 2 12.0001C2 10.6167 2.2625 9.32089 2.7875 8.11256C3.3125 6.90423 4.02917 5.84589 4.9375 4.93756C5.84583 4.02923 6.90833 3.31256 8.125 2.78756C9.34167 2.26256 10.6333 2.00006 12 2.00006C13.3833 2.00006 14.6792 2.26256 15.8875 2.78756C17.0958 3.31256 18.1542 4.02923 19.0625 4.93756C19.9708 5.84589 20.6875 6.90423 21.2125 8.11256C21.7375 9.32089 22 10.6167 22 12.0001C22 13.3667 21.7375 14.6584 21.2125 15.8751C20.6875 17.0917 19.9708 18.1542 19.0625 19.0626C18.1542 19.9709 17.0958 20.6876 15.8875 21.2126C14.6792 21.7376 13.3833 22.0001 12 22.0001ZM12 19.9501C12.4333 19.3501 12.8083 18.7251 13.125 18.0751C13.4417 17.4251 13.7 16.7334 13.9 16.0001H10.1C10.3 16.7334 10.5583 17.4251 10.875 18.0751C11.1917 18.7251 11.5667 19.3501 12 19.9501ZM9.4 19.5501C9.1 19.0001 8.8375 18.4292 8.6125 17.8376C8.3875 17.2459 8.2 16.6334 8.05 16.0001H5.1C5.58333 16.8334 6.1875 17.5584 6.9125 18.1751C7.6375 18.7917 8.46667 19.2501 9.4 19.5501ZM14.6 19.5501C15.5333 19.2501 16.3625 18.7917 17.0875 18.1751C17.8125 17.5584 18.4167 16.8334 18.9 16.0001H15.95C15.8 16.6334 15.6125 17.2459 15.3875 17.8376C15.1625 18.4292 14.9 19.0001 14.6 19.5501ZM4.25 14.0001H7.65C7.6 13.6667 7.5625 13.3376 7.5375 13.0126C7.5125 12.6876 7.5 12.3501 7.5 12.0001C7.5 11.6501 7.5125 11.3126 7.5375 10.9876C7.5625 10.6626 7.6 10.3334 7.65 10.0001H4.25C4.16667 10.3334 4.10417 10.6626 4.0625 10.9876C4.02083 11.3126 4 11.6501 4 12.0001C4 12.3501 4.02083 12.6876 4.0625 13.0126C4.10417 13.3376 4.16667 13.6667 4.25 14.0001ZM9.65 14.0001H14.35C14.4 13.6667 14.4375 13.3376 14.4625 13.0126C14.4875 12.6876 14.5 12.3501 14.5 12.0001C14.5 11.6501 14.4875 11.3126 14.4625 10.9876C14.4375 10.6626 14.4 10.3334 14.35 10.0001H9.65C9.6 10.3334 9.5625 10.6626 9.5375 10.9876C9.5125 11.3126 9.5 11.6501 9.5 12.0001C9.5 12.3501 9.5125 12.6876 9.5375 13.0126C9.5625 13.3376 9.6 13.6667 9.65 14.0001ZM16.35 14.0001H19.75C19.8333 13.6667 19.8958 13.3376 19.9375 13.0126C19.9792 12.6876 20 12.3501 20 12.0001C20 11.6501 19.9792 11.3126 19.9375 10.9876C19.8958 10.6626 19.8333 10.3334 19.75 10.0001H16.35C16.4 10.3334 16.4375 10.6626 16.4625 10.9876C16.4875 11.3126 16.5 11.6501 16.5 12.0001C16.5 12.3501 16.4875 12.6876 16.4625 13.0126C16.4375 13.3376 16.4 13.6667 16.35 14.0001ZM15.95 8.00006H18.9C18.4167 7.16673 17.8125 6.44173 17.0875 5.82506C16.3625 5.20839 15.5333 4.75006 14.6 4.45006C14.9 5.00006 15.1625 5.57089 15.3875 6.16256C15.6125 6.75423 15.8 7.36673 15.95 8.00006ZM10.1 8.00006H13.9C13.7 7.26673 13.4417 6.57506 13.125 5.92506C12.8083 5.27506 12.4333 4.65006 12 4.05006C11.5667 4.65006 11.1917 5.27506 10.875 5.92506C10.5583 6.57506 10.3 7.26673 10.1 8.00006ZM5.1 8.00006H8.05C8.2 7.36673 8.3875 6.75423 8.6125 6.16256C8.8375 5.57089 9.1 5.00006 9.4 4.45006C8.46667 4.75006 7.6375 5.20839 6.9125 5.82506C6.1875 6.44173 5.58333 7.16673 5.1 8.00006Z"
        fill="currentColor"
      />
    </g>
  </svg>

  <svg
    v-else-if="name === 'ios'"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="currentColor"
    xmlns="http://www.w3.org/2000/svg"
  >
    <mask
      id="mask0_211_6375"
      style="mask-type: alpha"
      maskUnits="userSpaceOnUse"
      x="0"
      y="0"
      width="24"
      height="24"
    >
      <rect
        y="6.10352e-05"
        width="24"
        height="24"
        fill="white"
      />
    </mask>
    <g mask="url(#mask0_211_6375)">
      <path
        d="M4 9.00006V7.00006H6V9.00006H4ZM4 17.0001V11.0001H6V17.0001H4ZM11 17.0001H9C8.45 17.0001 7.97917 16.8042 7.5875 16.4126C7.19583 16.0209 7 15.5501 7 15.0001V9.00006C7 8.45006 7.19583 7.97923 7.5875 7.58756C7.97917 7.19589 8.45 7.00006 9 7.00006H11C11.55 7.00006 12.0208 7.19589 12.4125 7.58756C12.8042 7.97923 13 8.45006 13 9.00006V15.0001C13 15.5501 12.8042 16.0209 12.4125 16.4126C12.0208 16.8042 11.55 17.0001 11 17.0001ZM9 15.0001H11V9.00006H9V15.0001ZM14 17.0001V15.0001H18V13.0001H16C15.45 13.0001 14.9792 12.8042 14.5875 12.4126C14.1958 12.0209 14 11.5501 14 11.0001V9.00006C14 8.45006 14.1958 7.97923 14.5875 7.58756C14.9792 7.19589 15.45 7.00006 16 7.00006H20V9.00006H16V11.0001H18C18.55 11.0001 19.0208 11.1959 19.4125 11.5876C19.8042 11.9792 20 12.4501 20 13.0001V15.0001C20 15.5501 19.8042 16.0209 19.4125 16.4126C19.0208 16.8042 18.55 17.0001 18 17.0001H14Z"
        fill="currentColor"
      />
    </g>
  </svg>

  <svg
    v-else-if="name === 'android'"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="currentColor"
    xmlns="http://www.w3.org/2000/svg"
  >
    <mask
      id="mask0_211_6376"
      style="mask-type: alpha"
      maskUnits="userSpaceOnUse"
      x="0"
      y="0"
      width="24"
      height="24"
    >
      <rect
        y="6.10352e-05"
        width="24"
        height="24"
        fill="white"
      />
    </mask>
    <g mask="url(#mask0_211_6376)">
      <path
        d="M5 11.0001V10.0001C5 8.80006 5.27083 7.70423 5.8125 6.71256C6.35417 5.72089 7.08333 4.90839 8 4.27506L6.125 2.40006L7 1.50006L9.125 3.62506C9.55833 3.42506 10.0208 3.27089 10.5125 3.16256C11.0042 3.05423 11.5 3.00006 12 3.00006C12.5 3.00006 12.9958 3.05423 13.4875 3.16256C13.9792 3.27089 14.4417 3.42506 14.875 3.62506L17 1.50006L17.875 2.40006L16 4.27506C16.9167 4.90839 17.6458 5.72089 18.1875 6.71256C18.7292 7.70423 19 8.80006 19 10.0001V11.0001H5ZM15 9.00006C15.2833 9.00006 15.5208 8.90423 15.7125 8.71256C15.9042 8.52089 16 8.28339 16 8.00006C16 7.71673 15.9042 7.47923 15.7125 7.28756C15.5208 7.09589 15.2833 7.00006 15 7.00006C14.7167 7.00006 14.4792 7.09589 14.2875 7.28756C14.0958 7.47923 14 7.71673 14 8.00006C14 8.28339 14.0958 8.52089 14.2875 8.71256C14.4792 8.90423 14.7167 9.00006 15 9.00006ZM9 9.00006C9.28333 9.00006 9.52083 8.90423 9.7125 8.71256C9.90417 8.52089 10 8.28339 10 8.00006C10 7.71673 9.90417 7.47923 9.7125 7.28756C9.52083 7.09589 9.28333 7.00006 9 7.00006C8.71667 7.00006 8.47917 7.09589 8.2875 7.28756C8.09583 7.47923 8 7.71673 8 8.00006C8 8.28339 8.09583 8.52089 8.2875 8.71256C8.47917 8.90423 8.71667 9.00006 9 9.00006ZM12 23.0001C10.05 23.0001 8.39583 22.3209 7.0375 20.9626C5.67917 19.6042 5 17.9501 5 16.0001V12.0001H19V16.0001C19 17.9501 18.3208 19.6042 16.9625 20.9626C15.6042 22.3209 13.95 23.0001 12 23.0001Z"
        fill="currentColor"
      />
    </g>
  </svg>

  <svg
    v-else-if="name === 'replay'"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="currentColor"
    xmlns="http://www.w3.org/2000/svg"
  >
    <mask
      id="mask0_71_8454"
      style="mask-type: alpha"
      maskUnits="userSpaceOnUse"
      x="0"
      y="0"
      width="24"
      height="24"
    >
      <rect
        y="6.10352e-05"
        width="24"
        height="24"
        fill="white"
      />
    </mask>
    <g mask="url(#mask0_71_8454)">
      <path
        d="M9.5 16.5V7.50005L16.5 12L9.5 16.5ZM12 23C10.2 23 8.5125 22.5875 6.9375 21.7625C5.3625 20.9375 4.05 19.7834 3 18.3V21H1V15H7V17H4.55C5.4 18.25 6.47917 19.2292 7.7875 19.9375C9.09583 20.6459 10.5 21 12 21C13.9167 21 15.6542 20.4501 17.2125 19.35C18.7708 18.25 19.8667 16.7917 20.5 14.975L22.45 15.425C21.7 17.6917 20.3667 19.5209 18.45 20.9125C16.5333 22.3042 14.3833 23 12 23ZM1.05 11C1.16667 9.88338 1.43333 8.81255 1.85 7.78755C2.26667 6.76255 2.84167 5.81672 3.575 4.95005L5 6.37505C4.46667 7.05838 4.03333 7.78755 3.7 8.56255C3.36667 9.33755 3.15833 10.15 3.075 11H1.05ZM6.4 4.97505L4.975 3.55005C5.85833 2.81672 6.80833 2.23755 7.825 1.81255C8.84167 1.38755 9.9 1.13338 11 1.05005V3.05005C10.15 3.13338 9.34167 3.34172 8.575 3.67505C7.80833 4.00838 7.08333 4.44172 6.4 4.97505ZM17.625 4.97505C16.9417 4.44172 16.2125 4.00838 15.4375 3.67505C14.6625 3.34172 13.85 3.13338 13 3.05005V1.05005C14.1167 1.15005 15.1875 1.40838 16.2125 1.82505C17.2375 2.24172 18.1833 2.81672 19.05 3.55005L17.625 4.97505ZM20.95 11C20.8667 10.15 20.6583 9.33755 20.325 8.56255C19.9917 7.78755 19.5583 7.05838 19.025 6.37505L20.45 4.95005C21.1833 5.81672 21.7583 6.76255 22.175 7.78755C22.5917 8.81255 22.85 9.88338 22.95 11H20.95Z"
        fill="currentColor"
      />
    </g>
  </svg>

  <svg
    v-else-if="name === 'settings'"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="currentColor"
    xmlns="http://www.w3.org/2000/svg"
  >
    <mask
      id="mask0_84_138"
      style="mask-type: alpha"
      maskUnits="userSpaceOnUse"
      x="0"
      y="0"
      width="24"
      height="24"
    >
      <rect
        y="6.10352e-05"
        width="24"
        height="24"
        fill="white"
      />
    </mask>
    <g mask="url(#mask0_84_138)">
      <path
        d="M9.24995 22.0001L8.84995 18.8001C8.63328 18.7167 8.42912 18.6167 8.23745 18.5001C8.04578 18.3834 7.85828 18.2584 7.67495 18.1251L4.69995 19.3751L1.94995 14.6251L4.52495 12.6751C4.50828 12.5584 4.49995 12.4459 4.49995 12.3376V11.6626C4.49995 11.5542 4.50828 11.4417 4.52495 11.3251L1.94995 9.37506L4.69995 4.62506L7.67495 5.87506C7.85828 5.74173 8.04995 5.61673 8.24995 5.50006C8.44995 5.38339 8.64995 5.28339 8.84995 5.20006L9.24995 2.00006H14.75L15.15 5.20006C15.3666 5.28339 15.5708 5.38339 15.7625 5.50006C15.9541 5.61673 16.1416 5.74173 16.325 5.87506L19.3 4.62506L22.05 9.37506L19.475 11.3251C19.4916 11.4417 19.5 11.5542 19.5 11.6626V12.3376C19.5 12.4459 19.4833 12.5584 19.45 12.6751L22.025 14.6251L19.275 19.3751L16.325 18.1251C16.1416 18.2584 15.95 18.3834 15.75 18.5001C15.55 18.6167 15.35 18.7167 15.15 18.8001L14.75 22.0001H9.24995ZM11 20.0001H12.975L13.325 17.3501C13.8416 17.2167 14.3208 17.0209 14.7625 16.7626C15.2041 16.5042 15.6083 16.1917 15.975 15.8251L18.45 16.8501L19.425 15.1501L17.275 13.5251C17.3583 13.2917 17.4166 13.0459 17.45 12.7876C17.4833 12.5292 17.5 12.2667 17.5 12.0001C17.5 11.7334 17.4833 11.4709 17.45 11.2126C17.4166 10.9542 17.3583 10.7084 17.275 10.4751L19.425 8.85006L18.45 7.15006L15.975 8.20006C15.6083 7.81673 15.2041 7.49589 14.7625 7.23756C14.3208 6.97923 13.8416 6.78339 13.325 6.65006L13 4.00006H11.025L10.675 6.65006C10.1583 6.78339 9.67912 6.97923 9.23745 7.23756C8.79578 7.49589 8.39162 7.80839 8.02495 8.17506L5.54995 7.15006L4.57495 8.85006L6.72495 10.4501C6.64162 10.7001 6.58328 10.9501 6.54995 11.2001C6.51662 11.4501 6.49995 11.7167 6.49995 12.0001C6.49995 12.2667 6.51662 12.5251 6.54995 12.7751C6.58328 13.0251 6.64162 13.2751 6.72495 13.5251L4.57495 15.1501L5.54995 16.8501L8.02495 15.8001C8.39162 16.1834 8.79578 16.5042 9.23745 16.7626C9.67912 17.0209 10.1583 17.2167 10.675 17.3501L11 20.0001ZM12.05 15.5001C13.0166 15.5001 13.8416 15.1584 14.525 14.4751C15.2083 13.7917 15.55 12.9667 15.55 12.0001C15.55 11.0334 15.2083 10.2084 14.525 9.52506C13.8416 8.84173 13.0166 8.50006 12.05 8.50006C11.0666 8.50006 10.2375 8.84173 9.56245 9.52506C8.88745 10.2084 8.54995 11.0334 8.54995 12.0001C8.54995 12.9667 8.88745 13.7917 9.56245 14.4751C10.2375 15.1584 11.0666 15.5001 12.05 15.5001Z"
        fill="currentColor"
      />
    </g>
  </svg>

  <svg
    v-else-if="name === 'profile'"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="currentColor"
    xmlns="http://www.w3.org/2000/svg"
  >
    <mask
      id="mask0_82_14757"
      style="mask-type: alpha"
      maskUnits="userSpaceOnUse"
      x="0"
      y="0"
      width="24"
      height="24"
    >
      <rect
        y="6.10352e-05"
        width="24"
        height="24"
        fill="white"
      />
    </mask>
    <g mask="url(#mask0_82_14757)">
      <path
        d="M12 12.0001C10.9 12.0001 9.95833 11.6084 9.175 10.8251C8.39167 10.0417 8 9.10006 8 8.00006C8 6.90006 8.39167 5.95839 9.175 5.17506C9.95833 4.39173 10.9 4.00006 12 4.00006C13.1 4.00006 14.0417 4.39173 14.825 5.17506C15.6083 5.95839 16 6.90006 16 8.00006C16 9.10006 15.6083 10.0417 14.825 10.8251C14.0417 11.6084 13.1 12.0001 12 12.0001ZM4 20.0001V17.2001C4 16.6334 4.14583 16.1126 4.4375 15.6376C4.72917 15.1626 5.11667 14.8001 5.6 14.5501C6.63333 14.0334 7.68333 13.6459 8.75 13.3876C9.81667 13.1292 10.9 13.0001 12 13.0001C13.1 13.0001 14.1833 13.1292 15.25 13.3876C16.3167 13.6459 17.3667 14.0334 18.4 14.5501C18.8833 14.8001 19.2708 15.1626 19.5625 15.6376C19.8542 16.1126 20 16.6334 20 17.2001V20.0001H4ZM6 18.0001H18V17.2001C18 17.0167 17.9542 16.8501 17.8625 16.7001C17.7708 16.5501 17.65 16.4334 17.5 16.3501C16.6 15.9001 15.6917 15.5626 14.775 15.3376C13.8583 15.1126 12.9333 15.0001 12 15.0001C11.0667 15.0001 10.1417 15.1126 9.225 15.3376C8.30833 15.5626 7.4 15.9001 6.5 16.3501C6.35 16.4334 6.22917 16.5501 6.1375 16.7001C6.04583 16.8501 6 17.0167 6 17.2001V18.0001ZM12 10.0001C12.55 10.0001 13.0208 9.80423 13.4125 9.41256C13.8042 9.02089 14 8.55006 14 8.00006C14 7.45006 13.8042 6.97923 13.4125 6.58756C13.0208 6.19589 12.55 6.00006 12 6.00006C11.45 6.00006 10.9792 6.19589 10.5875 6.58756C10.1958 6.97923 10 7.45006 10 8.00006C10 8.55006 10.1958 9.02089 10.5875 9.41256C10.9792 9.80423 11.45 10.0001 12 10.0001Z"
        fill="currentColor"
      />
    </g>
  </svg>

  <svg
    v-else-if="name === 'log out'"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="currentColor"
    xmlns="http://www.w3.org/2000/svg"
  >
    <mask
      id="mask0_222_3140"
      style="mask-type: alpha"
      maskUnits="userSpaceOnUse"
      x="0"
      y="0"
      width="24"
      height="24"
    >
      <rect
        y="6.10352e-05"
        width="24"
        height="24"
        fill="white"
      />
    </mask>
    <g mask="url(#mask0_222_3140)">
      <path
        d="M5 21.0001C4.45 21.0001 3.97917 20.8042 3.5875 20.4126C3.19583 20.0209 3 19.5501 3 19.0001V5.00006C3 4.45006 3.19583 3.97923 3.5875 3.58756C3.97917 3.19589 4.45 3.00006 5 3.00006H12V5.00006H5V19.0001H12V21.0001H5ZM16 17.0001L14.625 15.5501L17.175 13.0001H9V11.0001H17.175L14.625 8.45006L16 7.00006L21 12.0001L16 17.0001Z"
        fill="currentColor"
      />
    </g>
  </svg>

  <svg
    v-else-if="name === 'up'"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="currentColor"
    xmlns="http://www.w3.org/2000/svg"
  >
    <mask
      id="mask0_69_234"
      style="mask-type: alpha"
      maskUnits="userSpaceOnUse"
      x="0"
      y="0"
      width="24"
      height="24"
    >
      <rect
        y="6.10352e-05"
        width="24"
        height="24"
        fill="white"
      />
    </mask>
    <g mask="url(#mask0_69_234)">
      <path
        d="M6.00122 14.0001L12.0012 8.00006L18.0012 14.0001L16.6012 15.4001L12.0012 10.8001L7.40122 15.4001L6.00122 14.0001Z"
        fill="currentColor"
      />
    </g>
  </svg>

  <svg
    v-else-if="name === 'calendar'"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="currentColor"
    xmlns="http://www.w3.org/2000/svg"
  >
    <mask
      id="mask0_298_56"
      style="mask-type: alpha"
      maskUnits="userSpaceOnUse"
      x="0"
      y="0"
      width="24"
      height="24"
    >
      <rect
        y="6.10352e-05"
        width="24"
        height="24"
        fill="white"
      />
    </mask>
    <g mask="url(#mask0_298_56)">
      <path
        d="M5 22.0001C4.45 22.0001 3.97917 21.8042 3.5875 21.4126C3.19583 21.0209 3 20.5501 3 20.0001V6.00006C3 5.45006 3.19583 4.97923 3.5875 4.58756C3.97917 4.19589 4.45 4.00006 5 4.00006H6V2.00006H8V4.00006H16V2.00006H18V4.00006H19C19.55 4.00006 20.0208 4.19589 20.4125 4.58756C20.8042 4.97923 21 5.45006 21 6.00006V20.0001C21 20.5501 20.8042 21.0209 20.4125 21.4126C20.0208 21.8042 19.55 22.0001 19 22.0001H5ZM5 20.0001H19V10.0001H5V20.0001ZM5 8.00006H19V6.00006H5V8.00006Z"
        fill="currentColor"
      />
    </g>
  </svg>

  <svg
    v-else-if="name === 'phone'"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="currentColor"
    xmlns="http://www.w3.org/2000/svg"
  >
    <mask
      id="mask0_298_57"
      style="mask-type: alpha"
      maskUnits="userSpaceOnUse"
      x="0"
      y="0"
      width="24"
      height="24"
    >
      <rect
        y="6.10352e-05"
        width="24"
        height="24"
        fill="white"
      />
    </mask>
    <g mask="url(#mask0_298_57)">
      <path
        d="M7.85714 22.0001C7.34643 22.0001 6.90923 21.822 6.54554 21.466C6.18185 21.1099 6 20.6819 6 20.1819V3.81824C6 3.31824 6.18185 2.89021 6.54554 2.53415C6.90923 2.17809 7.34643 2.00006 7.85714 2.00006H17.1429C17.6536 2.00006 18.0908 2.17809 18.4545 2.53415C18.8182 2.89021 19 3.31824 19 3.81824V20.1819C19 20.6819 18.8182 21.1099 18.4545 21.466C18.0908 21.822 17.6536 22.0001 17.1429 22.0001H7.85714ZM7.85714 19.2728V20.1819H17.1429V19.2728H7.85714ZM7.85714 17.4546H17.1429V6.54552H7.85714V17.4546ZM7.85714 4.72733H17.1429V3.81824H7.85714V4.72733Z"
        fill="currentColor"
      />
    </g>
  </svg>

  <svg
    v-else-if="name === 'bug'"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="currentColor"
    xmlns="http://www.w3.org/2000/svg"
  >
    <mask
      id="mask0_298_58"
      style="mask-type: alpha"
      maskUnits="userSpaceOnUse"
      x="0"
      y="0"
      width="24"
      height="24"
    >
      <rect
        y="6.10352e-05"
        width="24"
        height="24"
        fill="white"
      />
    </mask>
    <g mask="url(#mask0_298_58)">
      <path
        d="M12 19.0001C13.1 19.0001 14.0417 18.6084 14.825 17.8251C15.6083 17.0417 16 16.1001 16 15.0001V11.0001C16 9.90006 15.6083 8.95839 14.825 8.17506C14.0417 7.39173 13.1 7.00006 12 7.00006C10.9 7.00006 9.95833 7.39173 9.175 8.17506C8.39167 8.95839 8 9.90006 8 11.0001V15.0001C8 16.1001 8.39167 17.0417 9.175 17.8251C9.95833 18.6084 10.9 19.0001 12 19.0001ZM10 16.0001H14V14.0001H10V16.0001ZM10 12.0001H14V10.0001H10V12.0001ZM12 21.0001C10.9167 21.0001 9.9125 20.7334 8.9875 20.2001C8.0625 19.6667 7.33333 18.9334 6.8 18.0001H4V16.0001H6.1C6.05 15.6667 6.02083 15.3334 6.0125 15.0001C6.00417 14.6667 6 14.3334 6 14.0001H4V12.0001H6C6 11.6667 6.00417 11.3334 6.0125 11.0001C6.02083 10.6667 6.05 10.3334 6.1 10.0001H4V8.00006H6.8C7.03333 7.61673 7.29583 7.25839 7.5875 6.92506C7.87917 6.59173 8.21667 6.30006 8.6 6.05006L7 4.40006L8.4 3.00006L10.55 5.15006C11.0167 5.00006 11.4917 4.92506 11.975 4.92506C12.4583 4.92506 12.9333 5.00006 13.4 5.15006L15.6 3.00006L17 4.40006L15.35 6.05006C15.7333 6.30006 16.0792 6.58756 16.3875 6.91256C16.6958 7.23756 16.9667 7.60006 17.2 8.00006H20V10.0001H17.9C17.95 10.3334 17.9792 10.6667 17.9875 11.0001C17.9958 11.3334 18 11.6667 18 12.0001H20V14.0001H18C18 14.3334 17.9958 14.6667 17.9875 15.0001C17.9792 15.3334 17.95 15.6667 17.9 16.0001H20V18.0001H17.2C16.6667 18.9334 15.9375 19.6667 15.0125 20.2001C14.0875 20.7334 13.0833 21.0001 12 21.0001Z"
        fill="currentColor"
      />
    </g>
  </svg>

  <svg
    v-else-if="name === 'document'"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="currentColor"
    xmlns="http://www.w3.org/2000/svg"
  >
    <mask
      id="mask0_298_59"
      style="mask-type: alpha"
      maskUnits="userSpaceOnUse"
      x="0"
      y="0"
      width="24"
      height="24"
    >
      <rect
        y="6.10352e-05"
        width="24"
        height="24"
        fill="white"
      />
    </mask>
    <g mask="url(#mask0_298_59)">
      <path
        d="M7 17.0001H14V15.0001H7V17.0001ZM7 13.0001H17V11.0001H7V13.0001ZM7 9.00006H17V7.00006H7V9.00006ZM5 21.0001C4.45 21.0001 3.97917 20.8042 3.5875 20.4126C3.19583 20.0209 3 19.5501 3 19.0001V5.00006C3 4.45006 3.19583 3.97923 3.5875 3.58756C3.97917 3.19589 4.45 3.00006 5 3.00006H19C19.55 3.00006 20.0208 3.19589 20.4125 3.58756C20.8042 3.97923 21 4.45006 21 5.00006V19.0001C21 19.5501 20.8042 20.0209 20.4125 20.4126C20.0208 20.8042 19.55 21.0001 19 21.0001H5ZM5 19.0001H19V5.00006H5V19.0001Z"
        fill="currentColor"
      />
    </g>
  </svg>

  <svg
    v-else-if="name === 'folder'"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="currentColor"
    xmlns="http://www.w3.org/2000/svg"
  >
    <mask
      id="mask0_folder_1"
      style="mask-type: alpha"
      maskUnits="userSpaceOnUse"
      x="0"
      y="0"
      width="24"
      height="24"
    >
      <rect
        y="6.10352e-05"
        width="24"
        height="24"
        fill="white"
      />
    </mask>
    <g mask="url(#mask0_folder_1)">
      <path
        d="M4 20C3.45 20 2.97917 19.8042 2.5875 19.4125C2.19583 19.0208 2 18.55 2 18V6C2 5.45 2.19583 4.97917 2.5875 4.5875C2.97917 4.19583 3.45 4 4 4H10L12 6H20C20.55 6 21.0208 6.19583 21.4125 6.5875C21.8042 6.97917 22 7.45 22 8V18C22 18.55 21.8042 19.0208 21.4125 19.4125C21.0208 19.8042 20.55 20 20 20H4ZM4 18H20V8H11.175L9.175 6H4V18Z"
        fill="currentColor"
      />
    </g>
  </svg>

  <svg
    v-else-if="name === 'play dashed'"
    viewBox="0 0 24 24"
    fill="currentColor"
    xmlns="http://www.w3.org/2000/svg"
  >
    <mask
      id="mask0_108_10658"
      style="mask-type: alpha"
      maskUnits="userSpaceOnUse"
      x="0"
      y="0"
      width="24"
      height="24"
    >
      <rect
        y="6.10352e-05"
        width="24"
        height="24"
        fill="white"
      />
    </mask>
    <g mask="url(#mask0_108_10658)">
      <path
        d="M4.25 18.3C3.61667 17.5667 3.10833 16.75 2.725 15.85C2.34167 14.95 2.1 14 2 13H4.05C4.15 13.7334 4.33333 14.4292 4.6 15.0875C4.86667 15.7459 5.21667 16.35 5.65 16.9L4.25 18.3ZM2 11C2.13333 10 2.38333 9.05005 2.75 8.15005C3.11667 7.25005 3.61667 6.43338 4.25 5.70005L5.65 7.10005C5.21667 7.65005 4.86667 8.25422 4.6 8.91255C4.33333 9.57088 4.15 10.2667 4.05 11H2ZM10.95 21.95C9.95 21.85 9.00417 21.6084 8.1125 21.225C7.22083 20.8417 6.4 20.35 5.65 19.75L7.05 18.3C7.63333 18.7334 8.24583 19.0917 8.8875 19.375C9.52917 19.6584 10.2167 19.85 10.95 19.95V21.95ZM7.1 5.70005L5.65 4.25005C6.4 3.65005 7.22083 3.15838 8.1125 2.77505C9.00417 2.39172 9.96667 2.15005 11 2.05005V4.05005C10.25 4.15005 9.55 4.34172 8.9 4.62505C8.25 4.90838 7.65 5.26672 7.1 5.70005ZM9.5 16.5V7.50005L16.5 12L9.5 16.5ZM13 21.95V19.95C15.0167 19.6667 16.6875 18.775 18.0125 17.275C19.3375 15.775 20 14.0167 20 12C20 9.98338 19.3375 8.22505 18.0125 6.72505C16.6875 5.22505 15.0167 4.33338 13 4.05005V2.05005C15.5667 2.33338 17.7083 3.41672 19.425 5.30005C21.1417 7.18338 22 9.41672 22 12C22 14.5834 21.1417 16.8167 19.425 18.7C17.7083 20.5834 15.5667 21.6667 13 21.95Z"
        fill="currentColor"
      />
    </g>
  </svg>

  <svg
    v-else-if="name === 'clock'"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="currentColor"
    xmlns="http://www.w3.org/2000/svg"
  >
    <mask
      id="mask0_409_5671"
      style="mask-type: alpha"
      maskUnits="userSpaceOnUse"
      x="0"
      y="0"
      width="24"
      height="24"
    >
      <rect
        y="6.10352e-05"
        width="24"
        height="24"
        fill="white"
      />
    </mask>
    <g mask="url(#mask0_409_5671)">
      <path
        d="M12.0001 22C10.7501 22 9.57927 21.7625 8.4876 21.2875C7.39593 20.8125 6.44593 20.1709 5.6376 19.3625C4.82926 18.5542 4.1876 17.6042 3.7126 16.5125C3.2376 15.4209 3.0001 14.25 3.0001 13C3.0001 11.75 3.2376 10.5792 3.7126 9.48754C4.1876 8.39587 4.82926 7.44587 5.6376 6.63754C6.44593 5.8292 7.39593 5.18754 8.4876 4.71254C9.57927 4.23754 10.7501 4.00004 12.0001 4.00004C13.2501 4.00004 14.4209 4.23754 15.5126 4.71254C16.6043 5.18754 17.5543 5.8292 18.3626 6.63754C19.1709 7.44587 19.8126 8.39587 20.2876 9.48754C20.7626 10.5792 21.0001 11.75 21.0001 13C21.0001 14.25 20.7626 15.4209 20.2876 16.5125C19.8126 17.6042 19.1709 18.5542 18.3626 19.3625C17.5543 20.1709 16.6043 20.8125 15.5126 21.2875C14.4209 21.7625 13.2501 22 12.0001 22ZM14.8001 17.2L16.2001 15.8L13.0001 12.6V8.00004H11.0001V13.4L14.8001 17.2ZM5.6001 2.35004L7.0001 3.75004L2.7501 8.00004L1.3501 6.60004L5.6001 2.35004ZM18.4001 2.35004L22.6501 6.60004L21.2501 8.00004L17.0001 3.75004L18.4001 2.35004ZM12.0001 20C13.9501 20 15.6043 19.3209 16.9626 17.9625C18.3209 16.6042 19.0001 14.95 19.0001 13C19.0001 11.05 18.3209 9.39587 16.9626 8.03754C15.6043 6.6792 13.9501 6.00004 12.0001 6.00004C10.0501 6.00004 8.39593 6.6792 7.0376 8.03754C5.67926 9.39587 5.0001 11.05 5.0001 13C5.0001 14.95 5.67926 16.6042 7.0376 17.9625C8.39593 19.3209 10.0501 20 12.0001 20Z"
        fill="currentColor"
      />
    </g>
  </svg>

  <svg
    v-else-if="name === 'description'"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="currentColor"
    xmlns="http://www.w3.org/2000/svg"
  >
    <mask
      id="mask0_75_8588"
      style="mask-type: alpha"
      maskUnits="userSpaceOnUse"
      x="0"
      y="0"
      width="24"
      height="24"
    >
      <rect
        y="6.10352e-05"
        width="24"
        height="24"
        fill="white"
      />
    </mask>
    <g mask="url(#mask0_75_8588)">
      <path
        d="M6 13H15V11H6V13ZM6 10H15V8H6V10ZM4 20C3.45 20 2.97917 19.8042 2.5875 19.4125C2.19583 19.0208 2 18.55 2 18V6C2 5.45 2.19583 4.97917 2.5875 4.5875C2.97917 4.19583 3.45 4 4 4H20C20.55 4 21.0208 4.19583 21.4125 4.5875C21.8042 4.97917 22 5.45 22 6V18C22 18.55 21.8042 19.0208 21.4125 19.4125C21.0208 19.8042 20.55 20 20 20H4ZM4 18H20V6H4V18Z"
        fill="currentColor"
      />
    </g>
  </svg>

  <svg
    v-else-if="name === 'down'"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="currentColor"
    xmlns="http://www.w3.org/2000/svg"
  >
    <mask
      id="mask0_69_239"
      style="mask-type: alpha"
      maskUnits="userSpaceOnUse"
      x="0"
      y="0"
      width="24"
      height="24"
    >
      <rect
        y="6.10352e-05"
        width="24"
        height="24"
        fill="white"
      />
    </mask>
    <g mask="url(#mask0_69_239)">
      <path
        d="M19 9.18919L12 16L5 9.18919L6.63333 7.6L12 12.8216L17.3667 7.6L19 9.18919Z"
        fill="currentColor"
      />
    </g>
  </svg>

  <svg
    v-else-if="name === 'refresh'"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="currentColor"
    xmlns="http://www.w3.org/2000/svg"
  >
    <mask
      id="mask0_563_5515"
      style="mask-type: alpha"
      maskUnits="userSpaceOnUse"
      x="0"
      y="0"
      width="24"
      height="24"
    >
      <rect
        y="6.10352e-05"
        width="24"
        height="24"
        fill="white"
      />
    </mask>
    <g mask="url(#mask0_563_5515)">
      <path
        d="M12.05 20.0001C9.81667 20.0001 7.91667 19.2251 6.35 17.6751C4.78333 16.1251 4 14.2334 4 12.0001V11.8251L2.4 13.4251L1 12.0251L5 8.02506L9 12.0251L7.6 13.4251L6 11.8251V12.0001C6 13.6667 6.5875 15.0834 7.7625 16.2501C8.9375 17.4167 10.3667 18.0001 12.05 18.0001C12.4833 18.0001 12.9083 17.9501 13.325 17.8501C13.7417 17.7501 14.15 17.6001 14.55 17.4001L16.05 18.9001C15.4167 19.2667 14.7667 19.5417 14.1 19.7251C13.4333 19.9084 12.75 20.0001 12.05 20.0001ZM19 15.9751L15 11.9751L16.4 10.5751L18 12.1751V12.0001C18 10.3334 17.4125 8.91673 16.2375 7.75006C15.0625 6.58339 13.6333 6.00006 11.95 6.00006C11.5167 6.00006 11.0917 6.05006 10.675 6.15006C10.2583 6.25006 9.85 6.40006 9.45 6.60006L7.95 5.10006C8.58333 4.73339 9.23333 4.45839 9.9 4.27506C10.5667 4.09173 11.25 4.00006 11.95 4.00006C14.1833 4.00006 16.0833 4.77506 17.65 6.32506C19.2167 7.87506 20 9.76673 20 12.0001V12.1751L21.6 10.5751L23 11.9751L19 15.9751Z"
        fill="currentColor"
      />
    </g>
  </svg>

  <svg
    v-else-if="name === 'search'"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <mask
      id="mask0_80_14707"
      style="mask-type: alpha"
      maskUnits="userSpaceOnUse"
      x="0"
      y="0"
      width="24"
      height="24"
    >
      <rect
        y="6.10352e-05"
        width="24"
        height="24"
        fill="white"
      />
    </mask>
    <g mask="url(#mask0_80_14707)">
      <path
        d="M19.6 21.0001L13.3 14.7001C12.8 15.1001 12.225 15.4167 11.575 15.6501C10.925 15.8834 10.2333 16.0001 9.5 16.0001C7.68333 16.0001 6.14583 15.3709 4.8875 14.1126C3.62917 12.8542 3 11.3167 3 9.50006C3 7.68339 3.62917 6.14589 4.8875 4.88756C6.14583 3.62923 7.68333 3.00006 9.5 3.00006C11.3167 3.00006 12.8542 3.62923 14.1125 4.88756C15.3708 6.14589 16 7.68339 16 9.50006C16 10.2334 15.8833 10.9251 15.65 11.5751C15.4167 12.2251 15.1 12.8001 14.7 13.3001L21 19.6001L19.6 21.0001ZM9.5 14.0001C10.75 14.0001 11.8125 13.5626 12.6875 12.6876C13.5625 11.8126 14 10.7501 14 9.50006C14 8.25006 13.5625 7.18756 12.6875 6.31256C11.8125 5.43756 10.75 5.00006 9.5 5.00006C8.25 5.00006 7.1875 5.43756 6.3125 6.31256C5.4375 7.18756 5 8.25006 5 9.50006C5 10.7501 5.4375 11.8126 6.3125 12.6876C7.1875 13.5626 8.25 14.0001 9.5 14.0001Z"
        fill="currentColor"
      />
    </g>
  </svg>

  <svg
    v-else-if="name === 'check'"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
  >
    <mask
      id="mask0_47_121"
      style="mask-type: alpha"
      maskUnits="userSpaceOnUse"
      x="0"
      y="0"
      width="24"
      height="24"
    >
      <rect
        y="6.10352e-05"
        width="24"
        height="24"
        fill="white"
      />
    </mask>
    <g mask="url(#mask0_47_121)">
      <path
        d="M9.5501 18L3.8501 12.3L5.2751 10.875L9.5501 15.15L18.7251 5.97504L20.1501 7.40004L9.5501 18Z"
        fill="currentColor"
      />
    </g>
  </svg>

  <svg
    v-else-if="name === 'cross'"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="currentColor"
    xmlns="http://www.w3.org/2000/svg"
  >
    <mask
      id="mask0_246_3965"
      style="mask-type: alpha"
      maskUnits="userSpaceOnUse"
      x="0"
      y="0"
      width="24"
      height="24"
    >
      <rect
        y="6.10352e-05"
        width="24"
        height="24"
        fill="white"
      />
    </mask>
    <g mask="url(#mask0_246_3965)">
      <path
        d="M7.2 18L6 16.8L10.8 12L6 7.2L7.2 6L12 10.8L16.8 6L18 7.2L13.2 12L18 16.8L16.8 18L12 13.2L7.2 18Z"
        fill="currentColor"
      />
    </g>
  </svg>

  <svg
    v-else-if="name === 'search grid'"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <mask
      id="mask0_33_490"
      style="mask-type: alpha"
      maskUnits="userSpaceOnUse"
      x="0"
      y="0"
      width="24"
      height="24"
    >
      <rect
        y="6.10352e-05"
        width="24"
        height="24"
        fill="white"
      />
    </mask>
    <g mask="url(#mask0_33_490)">
      <path
        d="M21.6 23.0001L18.525 19.9501C18.225 20.1334 17.9042 20.2709 17.5625 20.3626C17.2208 20.4542 16.8667 20.5001 16.5 20.5001C15.4 20.5001 14.4583 20.1084 13.675 19.3251C12.8917 18.5417 12.5 17.6001 12.5 16.5001C12.5 15.4001 12.8917 14.4584 13.675 13.6751C14.4583 12.8917 15.4 12.5001 16.5 12.5001C17.6 12.5001 18.5417 12.8917 19.325 13.6751C20.1083 14.4584 20.5 15.4001 20.5 16.5001C20.5 16.8834 20.45 17.2459 20.35 17.5876C20.25 17.9292 20.1083 18.2501 19.925 18.5501L23 21.6001L21.6 23.0001ZM5.5 20.5001C4.4 20.5001 3.45833 20.1084 2.675 19.3251C1.89167 18.5417 1.5 17.6001 1.5 16.5001C1.5 15.4001 1.89167 14.4584 2.675 13.6751C3.45833 12.8917 4.4 12.5001 5.5 12.5001C6.6 12.5001 7.54167 12.8917 8.325 13.6751C9.10833 14.4584 9.5 15.4001 9.5 16.5001C9.5 17.6001 9.10833 18.5417 8.325 19.3251C7.54167 20.1084 6.6 20.5001 5.5 20.5001ZM5.5 18.5001C6.05 18.5001 6.52083 18.3042 6.9125 17.9126C7.30417 17.5209 7.5 17.0501 7.5 16.5001C7.5 15.9501 7.30417 15.4792 6.9125 15.0876C6.52083 14.6959 6.05 14.5001 5.5 14.5001C4.95 14.5001 4.47917 14.6959 4.0875 15.0876C3.69583 15.4792 3.5 15.9501 3.5 16.5001C3.5 17.0501 3.69583 17.5209 4.0875 17.9126C4.47917 18.3042 4.95 18.5001 5.5 18.5001ZM16.5 18.5001C17.05 18.5001 17.5208 18.3042 17.9125 17.9126C18.3042 17.5209 18.5 17.0501 18.5 16.5001C18.5 15.9501 18.3042 15.4792 17.9125 15.0876C17.5208 14.6959 17.05 14.5001 16.5 14.5001C15.95 14.5001 15.4792 14.6959 15.0875 15.0876C14.6958 15.4792 14.5 15.9501 14.5 16.5001C14.5 17.0501 14.6958 17.5209 15.0875 17.9126C15.4792 18.3042 15.95 18.5001 16.5 18.5001ZM5.5 9.50006C4.4 9.50006 3.45833 9.10839 2.675 8.32506C1.89167 7.54173 1.5 6.60006 1.5 5.50006C1.5 4.40006 1.89167 3.45839 2.675 2.67506C3.45833 1.89173 4.4 1.50006 5.5 1.50006C6.6 1.50006 7.54167 1.89173 8.325 2.67506C9.10833 3.45839 9.5 4.40006 9.5 5.50006C9.5 6.60006 9.10833 7.54173 8.325 8.32506C7.54167 9.10839 6.6 9.50006 5.5 9.50006ZM16.5 9.50006C15.4 9.50006 14.4583 9.10839 13.675 8.32506C12.8917 7.54173 12.5 6.60006 12.5 5.50006C12.5 4.40006 12.8917 3.45839 13.675 2.67506C14.4583 1.89173 15.4 1.50006 16.5 1.50006C17.6 1.50006 18.5417 1.89173 19.325 2.67506C20.1083 3.45839 20.5 4.40006 20.5 5.50006C20.5 6.60006 20.1083 7.54173 19.325 8.32506C18.5417 9.10839 17.6 9.50006 16.5 9.50006ZM5.5 7.50006C6.05 7.50006 6.52083 7.30423 6.9125 6.91256C7.30417 6.52089 7.5 6.05006 7.5 5.50006C7.5 4.95006 7.30417 4.47923 6.9125 4.08756C6.52083 3.69589 6.05 3.50006 5.5 3.50006C4.95 3.50006 4.47917 3.69589 4.0875 4.08756C3.69583 4.47923 3.5 4.95006 3.5 5.50006C3.5 6.05006 3.69583 6.52089 4.0875 6.91256C4.47917 7.30423 4.95 7.50006 5.5 7.50006ZM16.5 7.50006C17.05 7.50006 17.5208 7.30423 17.9125 6.91256C18.3042 6.52089 18.5 6.05006 18.5 5.50006C18.5 4.95006 18.3042 4.47923 17.9125 4.08756C17.5208 3.69589 17.05 3.50006 16.5 3.50006C15.95 3.50006 15.4792 3.69589 15.0875 4.08756C14.6958 4.47923 14.5 4.95006 14.5 5.50006C14.5 6.05006 14.6958 6.52089 15.0875 6.91256C15.4792 7.30423 15.95 7.50006 16.5 7.50006Z"
        fill="currentColor"
      />
    </g>
  </svg>

  <svg
    v-else-if="name === 'trash'"
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    stroke-width="1.5"
    stroke="currentColor"
  >
    <path
      stroke-linecap="round"
      stroke-linejoin="round"
      d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0"
    />
  </svg>

  <svg
    v-else-if="name === 'play'"
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    stroke-width="1.5"
    stroke="currentColor"
  >
    <path
      stroke-linecap="round"
      stroke-linejoin="round"
      d="M5.25 5.653c0-.856.917-1.398 1.667-.986l11.54 6.347a1.125 1.125 0 0 1 0 1.972l-11.54 6.347a1.125 1.125 0 0 1-1.667-.986V5.653Z"
    />
  </svg>

  <!-- Same triangle as "play", solid: at button size the outline reads as an
       empty arrow, so the design fills it. -->
  <svg
    v-else-if="name === 'play filled'"
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="currentColor"
  >
    <path
      d="M5.25 5.653c0-.856.917-1.398 1.667-.986l11.54 6.347a1.125 1.125 0 0 1 0 1.972l-11.54 6.347a1.125 1.125 0 0 1-1.667-.986V5.653Z"
    />
  </svg>

  <svg
    v-else-if="name === 'pause'"
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    stroke-width="1.5"
    stroke="currentColor"
  >
    <path
      stroke-linecap="round"
      stroke-linejoin="round"
      d="M15.75 5.25v13.5m-7.5-13.5v13.5"
    />
  </svg>
  <svg
    v-else-if="name === 'upload dashed'"
    width="28"
    height="28"
    viewBox="0 0 28 28"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M3.6665 22.3999C2.82206 21.3999 2.14428 20.2999 1.63317 19.0999C1.12206 17.8999 0.799837 16.6444 0.666504 15.3333H3.39984C3.53317 16.2888 3.77762 17.2055 4.13317 18.0833C4.48873 18.9611 4.95539 19.7777 5.53317 20.5333L3.6665 22.3999ZM0.666504 12.6666C0.844282 11.3555 1.17762 10.0999 1.6665 8.89994C2.15539 7.69994 2.82206 6.59994 3.6665 5.59994L5.53317 7.46661C4.95539 8.22217 4.48873 9.03883 4.13317 9.91661C3.77762 10.7944 3.53317 11.7111 3.39984 12.6666H0.666504ZM12.5998 27.2666C11.2887 27.1333 10.0387 26.8166 8.84984 26.3166C7.66095 25.8166 6.55539 25.1555 5.53317 24.3333L7.39984 22.3999C8.17762 22.9777 8.99984 23.4555 9.8665 23.8333C10.7332 24.2111 11.6443 24.4666 12.5998 24.5999V27.2666ZM7.4665 5.59994L5.53317 3.66661C6.57762 2.84439 7.69984 2.18328 8.89984 1.68328C10.0998 1.18328 11.3554 0.86661 12.6665 0.733276V3.39994C11.7109 3.53328 10.7943 3.78883 9.9165 4.16661C9.03873 4.54439 8.22206 5.02217 7.4665 5.59994ZM15.2665 27.2666V24.5999C16.2443 24.4666 17.1721 24.2166 18.0498 23.8499C18.9276 23.4833 19.7554 22.9999 20.5332 22.3999L22.4665 24.3333C21.4221 25.1777 20.2943 25.8444 19.0832 26.3333C17.8721 26.8222 16.5998 27.1333 15.2665 27.2666ZM20.5998 5.59994C19.8221 5.02217 18.9887 4.54439 18.0998 4.16661C17.2109 3.78883 16.2887 3.53328 15.3332 3.39994V0.733276C16.6443 0.86661 17.9054 1.18328 19.1165 1.68328C20.3276 2.18328 21.4443 2.84439 22.4665 3.66661L20.5998 5.59994ZM24.3332 22.3999L22.4665 20.5333C23.0443 19.7777 23.5109 18.9611 23.8665 18.0833C24.2221 17.2055 24.4665 16.2888 24.5998 15.3333H27.3332C27.1554 16.6444 26.8221 17.8999 26.3332 19.0999C25.8443 20.2999 25.1776 21.3999 24.3332 22.3999ZM24.5998 12.6666C24.4665 11.7111 24.2221 10.7944 23.8665 9.91661C23.5109 9.03883 23.0443 8.22217 22.4665 7.46661L24.3332 5.59994C25.1776 6.59994 25.8554 7.69994 26.3665 8.89994C26.8776 10.0999 27.1998 11.3555 27.3332 12.6666H24.5998ZM12.6998 20.6666V12.4333L9.23317 15.8999L7.3665 13.9999L14.0332 7.33328L20.6998 13.9999L18.7998 15.8666L15.3665 12.4333V20.6666H12.6998Z"
      fill="currentColor"
    />
  </svg>
  <!-- Nav glyphs from the Tapioka dark redesign. Unlike the filled Material
       icons above these are stroked outlines on a 24 box; they are additive on
       purpose, so the older names other views still use keep their artwork. -->
  <svg
    v-else-if="name === 'grid'"
    v-bind="strokedIconAttrs"
  >
    <rect
      x="3"
      y="3"
      width="8"
      height="8"
      rx="2"
    />
    <rect
      x="13"
      y="3"
      width="8"
      height="8"
      rx="2"
    />
    <rect
      x="3"
      y="13"
      width="8"
      height="8"
      rx="2"
    />
    <rect
      x="13"
      y="13"
      width="8"
      height="8"
      rx="2"
    />
  </svg>
  <svg
    v-else-if="name === 'bar chart'"
    v-bind="strokedIconAttrs"
  >
    <path d="M5 20V10M12 20V4M19 20v-7" />
  </svg>
  <!-- The redesign's take on "list": three plain rules, where the legacy "list"
       glyph draws boxes-and-rules. Kept apart rather than replacing "list",
       which still serves the not-yet-redesigned batch detail. -->
  <svg
    v-else-if="name === 'list lines'"
    v-bind="strokedIconAttrs"
  >
    <path d="M4 6h16M4 12h16M4 18h10" />
  </svg>
  <svg
    v-else-if="name === 'menu'"
    v-bind="strokedIconAttrs"
  >
    <path d="M4 6h16M4 12h16M4 18h16" />
  </svg>
  <svg
    v-else-if="name === 'more'"
    v-bind="strokedIconAttrs"
  >
    <path
      d="M5 12h.01M12 12h.01M19 12h.01"
      stroke-width="3"
    />
  </svg>
  <svg
    v-else-if="name === 'grip'"
    v-bind="strokedIconAttrs"
  >
    <path
      d="M9 6h.01M15 6h.01M9 12h.01M15 12h.01M9 18h.01M15 18h.01"
      stroke-width="3"
    />
  </svg>
  <!-- Descending rules with an arrow: the sort control's glyph, taken from the
       design. `sort ascending` / `sort descending` sit in IconName with nothing
       behind them (twelve names do — see the note on the union below), so this
       lands under its own name rather than reviving one of those. -->
  <svg
    v-else-if="name === 'sort'"
    v-bind="strokedIconAttrs"
  >
    <path d="M3 7h11M3 12h7M3 17h4" />
    <path d="M17 5v14M17 5l-3.5 3.5M17 5l3.5 3.5" />
  </svg>
  <!-- `left` and `right` were declared in IconName long before anything drew
       them, so the type promised two glyphs that rendered nothing. The pager
       needs both; drawn as bare chevrons to match the stroked redesign set. -->
  <svg
    v-else-if="name === 'left'"
    v-bind="strokedIconAttrs"
  >
    <path d="M14.5 6 8.5 12l6 6" />
  </svg>
  <svg
    v-else-if="name === 'right'"
    v-bind="strokedIconAttrs"
  >
    <path d="M9.5 6l6 6-6 6" />
  </svg>
  <svg
    v-else-if="name === 'chevrons left'"
    v-bind="strokedIconAttrs"
  >
    <path d="M13 6l-6 6 6 6M18 6l-6 6 6 6" />
  </svg>
  <svg
    v-else-if="name === 'chevrons right'"
    v-bind="strokedIconAttrs"
  >
    <path d="M11 6l6 6-6 6M6 6l6 6-6 6" />
  </svg>
  <svg
    v-else-if="name === 'flask'"
    v-bind="strokedIconAttrs"
  >
    <path d="M9 3h6M10 3v6.5L4.6 18a2 2 0 0 0 1.7 3h11.4a2 2 0 0 0 1.7-3L14 9.5V3" />
  </svg>
  <svg
    v-else-if="name === 'calendar clock'"
    v-bind="strokedIconAttrs"
  >
    <rect
      x="3"
      y="4"
      width="14"
      height="16"
      rx="2"
    />
    <path d="M3 9h14" />
    <path d="M7 2v4" />
    <circle
      cx="17.5"
      cy="16.5"
      r="4.5"
    />
    <path d="M17.5 14.5v2l1.5 1" />
  </svg>
  <svg
    v-else-if="name === 'bolt'"
    v-bind="strokedIconAttrs"
  >
    <path d="M13 2L4 14h6l-1 8 10-12h-6l1-8z" />
  </svg>
  <svg
    v-else-if="name === 'device'"
    v-bind="strokedIconAttrs"
  >
    <rect
      x="7"
      y="2"
      width="10"
      height="20"
      rx="2"
    />
    <path d="M11 18h2" />
  </svg>
  <!-- An isometric box for Artifacts: the shipped build, the thing you store. -->
  <svg
    v-else-if="name === 'box'"
    v-bind="strokedIconAttrs"
  >
    <path
      d="M21 8.2v7.6a1.6 1.6 0 0 1-.85 1.41l-7.4 4.06a1.6 1.6 0 0 1-1.5 0l-7.4-4.06A1.6 1.6 0 0 1 3 15.8V8.2a1.6 1.6 0 0 1 .85-1.41l7.4-4.06a1.6 1.6 0 0 1 1.5 0l7.4 4.06A1.6 1.6 0 0 1 21 8.2Z"
    />
    <path d="m3.3 7.4 8.7 4.75 8.7-4.75" />
    <path d="M12 12.15V21.5" />
  </svg>
  <svg
    v-else-if="name === 'user'"
    v-bind="strokedIconAttrs"
  >
    <circle
      cx="12"
      cy="8"
      r="3.5"
    />
    <path d="M4.5 20c1.5-4 4.5-6 7.5-6s6 2 7.5 6" />
  </svg>
  <svg
    v-else-if="name === 'sign out'"
    v-bind="strokedIconAttrs"
  >
    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
    <path d="M16 17l5-5-5-5" />
    <path d="M21 12H9" />
  </svg>
  <!-- Import and export as one mirrored pair, the way the design draws them:
       the arrow lands on the tray to bring a file in, and lifts off it to send
       one out. Same tray, opposite arrows — that is what tells the two buttons
       apart when they sit on neighbouring screens. -->
  <svg
    v-else-if="name === 'download'"
    v-bind="strokedIconAttrs"
  >
    <path d="M12 15V3m0 12l-4-4m4 4l4-4M5 21h14" />
  </svg>
  <svg
    v-else-if="name === 'upload'"
    v-bind="strokedIconAttrs"
  >
    <path d="M12 3v12m0-12l-4 4m4-4l4 4M5 21h14" />
  </svg>
  <!-- The three app-start modes, drawn as one family so the list reads as a
       gradient of how much the runner touches the app: a full drop onto a clean
       slate, a turn back to the start, and nothing at all. `reset keep` is
       deliberately `reset clear data` minus the bar — losing the bar is what
       says "no clean slate". Traced from the design (N7AIT-1182); the stroked
       `reset restart` is not `refresh`, which is a filled Material glyph and
       would sit at a different weight next to these two. -->
  <svg
    v-else-if="name === 'reset clear data'"
    v-bind="strokedIconAttrs"
  >
    <path
      d="M5 3.5h14"
      stroke-width="2.4"
    />
    <path d="M12 8.5v11" />
    <path d="M7 14.5l5 6 5-6" />
  </svg>
  <svg
    v-else-if="name === 'reset restart'"
    v-bind="strokedIconAttrs"
  >
    <path d="M15.85 15.9a6.5 6.5 0 1 1-1.2-9" />
    <path d="M19.85 6.4v5h-5" />
  </svg>
  <svg
    v-else-if="name === 'reset keep'"
    v-bind="strokedIconAttrs"
  >
    <path d="M12 3.5v16" />
    <path d="M7 14.5l5 6 5-6" />
  </svg>
  <!-- Four-point star: the design's mark for anything the AI produced. -->
  <svg
    v-else-if="name === 'sparkle'"
    v-bind="strokedIconAttrs"
  >
    <path d="M12 2l2.5 6.5L21 11l-6.5 2.5L12 20l-2.5-6.5L3 11l6.5-2.5L12 2z" />
  </svg>
  <svg
    v-else-if="name === 'sun'"
    v-bind="strokedIconAttrs"
  >
    <circle
      cx="12"
      cy="12"
      r="4"
    />
    <path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" />
  </svg>
  <svg
    v-else-if="name === 'moon'"
    v-bind="strokedIconAttrs"
  >
    <path d="M21 12.8A9 9 0 1 1 11.2 3 7 7 0 0 0 21 12.8z" />
  </svg>
  <!-- Straight from the design: a circled dollar drawn as strokes, unlike
       PrimeIcons' filled `pi-dollar`, which blots at footer size. -->
  <svg
    v-else-if="name === 'cost'"
    v-bind="strokedIconAttrs"
  >
    <circle
      cx="12"
      cy="12"
      r="9"
    />
    <path
      d="M14.8 9.2a2.5 2.5 0 0 0-2.3-1.5c-1.5 0-2.5.8-2.5 2s1 1.6 2.5 1.9c1.5.3 2.5.8 2.5 2s-1 2-2.5 2a2.7 2.7 0 0 1-2.5-1.5M12 6.5v1.2M12 16.3v1.2"
    />
  </svg>
  <svg
    v-else-if="name === 'lock'"
    v-bind="strokedIconAttrs"
  >
    <rect
      x="4"
      y="10.5"
      width="16"
      height="10"
      rx="2.5"
    />
    <path d="M8 10.5v-3a4 4 0 0 1 8 0v3" />
  </svg>
  <svg
    v-else-if="name === 'copy'"
    v-bind="strokedIconAttrs"
  >
    <rect
      x="9"
      y="9"
      width="12"
      height="12"
      rx="2.5"
    />
    <path d="M15 9V5.5A2.5 2.5 0 0 0 12.5 3h-7A2.5 2.5 0 0 0 3 5.5v7A2.5 2.5 0 0 0 5.5 15H9" />
  </svg>
</template>
